import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useLanguage } from "hooks/settings";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { OverviewCard } from "./OverviewCard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import dayjs, { Dayjs } from "dayjs";
import { DeliveryPaceTable } from "components/strategyDashboard/deliveries/DeliveryPaceTable";
import { useGetStrategyDashboardDeliveriesOverviewQuery } from "src/store/api/generatedApi";
import { loadedEndpoint } from "models/loaded";
import { formatTimestampWithOffset } from "src/utils/formatTimestampWithOffset";

export const Deliveries = ({ date }: { date: Dayjs }) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const language = useLanguage();
  dayjs.locale(language);
  const monthName = date.format("MMMM");

  const loadedData = loadedEndpoint(
    useGetStrategyDashboardDeliveriesOverviewQuery({
      tenantName: tenant,
      on: formatTimestampWithOffset(date),
    })
  );

  const latestDeliveryDate =
    loadedData.status === "success" && loadedData.data.delivery_series[0]
      ? dayjs(loadedData.data.delivery_series[0].delivered_date).format("LL")
      : null;

  const isFetching =
    loadedData.status === "success" ? loadedData.fetching : false;

  const headerContent = (
    <Stack textAlign="right" sx={{ ml: "auto" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("latestDelivery")}
      </Typography>
      {isFetching ? (
        <Skeleton variant="text" width={80} />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: "fontWeightSemiBold" }}>
          {latestDeliveryDate ?? "-"}
        </Typography>
      )}
    </Stack>
  );

  const showPacePercent = useIsBehindFeatureFlag(
    "strategy_dashboard_delivery_pace_percent"
  );

  return (
    <OverviewCard
      title={t("monthDeliveries", { month: monthName })}
      icon={LocalShippingIcon}
      headerContent={headerContent}
      buttonText={t("viewDeliveries")}
      buttonHref={`/${tenant}/dashboard/deliveries`}
    >
      <Box sx={{ height: "94%", px: 2 }}>
        <DeliveryPaceTable
          loadedData={loadedData}
          showPacePercent={showPacePercent}
          showRemainingMass={false}
          fillHeight
        />
      </Box>
    </OverviewCard>
  );
};
